import React, {Component} from 'react';
import Resize from 'react-resize-detector';
import PropTypes from 'prop-types';

import * as d3 from 'd3';
import _ from 'lodash';

import HoverOver from '../HoverOver';
import './LineChart.css';

class LineChart extends Component {

  constructor(props) {
    super(props);

    this.state = {
      margin: {left: 30, right: 10, top: 20, bottom: 20},
      width: 500,
      height: props.height || 250
    };
  }

  componentDidMount() {
    this.handelResize();
  }

  componentDidUpdate() {
    setTimeout(this.renderD3.bind(this), 100);
  }

  handelResize() {
    const bounds = d3.select(this.refs.wrapper).node().getBoundingClientRect();
    const width = bounds.width || 100;
    const height = this.props.height || 250;

    let mergedState = _.merge({}, this.state, {
      width: width,
      height: height
    });

    this.setState(mergedState);
  }

  renderD3() {
    const width = this.state.width - this.state.margin.left - this.state.margin.right;
    const height = this.state.height - this.state.margin.top - this.state.margin.bottom;
    const props = this.props;
    const self = this;

    if (!props.data || !props.data.length) {
      return;
    }

    const parseYear = d3.timeParse("%Y");

    const legendLabel = g => _.get(props, ['labels', g], g);

    const valueG = row => _.get(row, props.dimG, 0);
    const valueY = row => _.get(row, props.dimY, 0);
    const valueX = row => {
      return parseYear(_.get(row, props.dimX, 0))
    };

    const valuesG = _.chain(props.data).map(valueG).uniq().value();
    const valuesX = _.chain(props.data).map(valueX).uniq().value();

    const x = d3.scaleTime()
      .domain(d3.extent(valuesX))
      .range([0, width]);

    const axisX = d3.axisBottom(x)
      .tickSizeInner(height, 0)
      .tickSizeOuter(0)
      .tickPadding(10);

    const y = d3.scaleLinear()
      .domain([0, d3.max(props.data, valueY)])
      .range([height, 0]);

    const axisY = d3.axisLeft(y)
      .tickSizeInner(width, 0)
      .tickSizeOuter(0)
      .tickPadding(10)
      .ticks(6);

    const colors = d3.scaleOrdinal()
      .domain(valuesG)
      .range(["#5d434c", "#c85a5a", "#64acbe", "#ad9ea5"]);

    const line = d3.line()
      .curve(d3.curveMonotoneX)
      .x(d => x(valueX(d)))
      .y(d => y(valueY(d)));

    // Add the X Axis
    d3.select(this.refs.xAxis)
      .transition()
      .call(axisX);

    // Add the Y Axis
    d3.select(this.refs.yAxis)
      .transition()
      .call(axisY);

    // Add Lines
    const lines = d3.select(this.refs.lines)
      .selectAll("path")
      .data(_.reduce(valuesG, (output, value) => {
        output.push(_.filter(props.data, x => valueG(x) === value));
        return output;
      }, []));

    lines.enter().append("path").each(createLines);
    lines.each(createLines);
    lines.exit().remove();

    function createLines() {
      d3.select(this)
        .attr("class", "line")
        .style("stroke", function (d) {
          return colors(valueG(_.first(d)));
        })
        .transition()
        .attr("d", function (d) {
          return line(d);
        })
    }

    // Add Dots
    const dots = d3.select(this.refs.dots)
      .selectAll("circle")
      .data(props.data);

    dots.enter().append("circle").each(createDots);
    dots.each(createDots);
    dots.exit().remove();

    function createDots() {
      d3.select(this)
        .attr("r", 4)
        .style("fill", d => colors(valueG(d)))
        .on('mouseenter', d => {
          if (props.hover) {
            d3.selectAll("#FIPS_" + d.id).attr("class", "hover_over");
          }
          self.setState({hover: createHover(d)})
        })
        .on("mouseout", d => {
          if (props.hover) {
            d3.selectAll("#FIPS_" + d.id).attr("class", "hover_out");
          }
          self.setState({hover: null})
        })
        .on("click", d => props.onClick ? props.onClick(d) : null)
        .transition()
        .attr("cx", d => x(valueX(d)))
        .attr("cy", d => y(valueY(d)));
    }

    function createHover(d) {
      const xc = x(valueX(d)) + self.state.margin.left;
      const yc = y(valueY(d)) + self.state.margin.top;
      const content = (
        <div>
          <h4>{valueG(d)}</h4>
          <div><b>{_.get(props, 'titleX', props.dimX)}</b> : <i>{_.get(d, props.dimX)}</i></div>
          <div><b>{_.get(props, 'titleY', props.dimY)}</b> : <i>{d3.format(',')(valueY(d))}</i></div>
        </div>
      );

      return {x: xc, y: yc, content};
    }

    // Clear and redraw legend
    if (props.highlightX) {
      d3.select(this.refs.highlight)
        .attr('class', 'highlight-bar')
        .attr('width', 2)
        .attr("rx", 1)
        .attr("ry", 1)
        .attr('height', height)
        .transition()
        .duration(300)
        .attr('transform', "translate(" + (x(parseYear(props.highlightX)) - 1) + ",0)")
    }


    // Clear and redraw legend
    d3.select(this.refs.legend).selectAll('*').remove();

    const legend = d3.select(this.refs.legend)
      .selectAll("g")
      .data(_.uniq(valuesG))
      .enter().append("g")
      .attr("transform", function (d, i) {
        return "translate(10," + i * 15 + ")";
      });

    legend.append("rect")
      .attr("rx", 6)
      .attr("ry", 6)
      .attr("width", 12)
      .attr("height", 12)
      .attr("fill", d => colors(d));

    legend.append("text")
      .attr("x", 15)
      .attr("y", 6)
      .attr("dy", "0.32em")
      .text(legendLabel);
  }

  render() {
    const innerWidth = this.state.width - this.state.margin.left - this.state.margin.right;

    return (
      <div ref="wrapper" className="LineChart" height={this.state.height}>
        <Resize handleWidth onResize={this.handelResize.bind(this)}/>
        <svg width={this.state.width} height={this.state.height}>
          <g transform={"translate(" + this.state.margin.left + "," + this.state.margin.top + ")"} ref="svg">
            <g className="axis">
              <g ref="xAxis"></g>
              <g ref="yAxis" transform={"translate(" + innerWidth + ",0)"}></g>
            </g>
            <g>
              <rect ref="highlight"></rect>
            </g>
            <g ref="lines"></g>
            <g ref="dots"></g>
            <g ref="legend" className="legend"></g>
          </g>
        </svg>
        <HoverOver data={this.state.hover} {...this.state.margin}/>
      </div>
    );
  }
}

LineChart.propTypes = {
  dimG: PropTypes.string,
  dimX: PropTypes.string,
  dimY: PropTypes.string,
  highlightX: PropTypes.number,
  data: PropTypes.array,
  onClick: PropTypes.func
};

export default LineChart;




// WEBPACK FOOTER //
// ./src/charts/LineChart/index.js